import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap, throwError, from } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthService } from '../auth/services/auth.service';
import {
  clearAuthSession,
  getAccessToken,
} from '../auth/auth-session.util';
import {
  authEpochStore,
  isSessionResultCurrent,
} from '../auth/auth-epoch.store';

/**
 * Office auth routes whose own proof IS the credential.
 *
 * `verify-login-otp` / `resend-login-otp` carry the challenge token and
 * `select-branch` carries the 5-minute selection token: all three are `@Public`
 * and all three run BEFORE any session exists, so a stale `Authorization`
 * header is at best noise and a 401 refresh on them is meaningless.
 *
 * `switch-branch` is deliberately NOT here — it is `@Roles('branch','office')`
 * and needs the Bearer token and the ordinary 401 refresh.
 */
function isPublicOfficeProofUrl(url: string): boolean {
  return /\/auth\/office\/(verify-login-otp|resend-login-otp|select-branch)(?:[?#]|$)/i.test(
    url,
  );
}

function isAuthExemptUrl(url: string): boolean {
  const u = url.toLowerCase();
  return (
    u.includes('/auth/refresh-token') ||
    u.includes('/auth/logout') ||
    u.includes('/auth/office/login') ||
    isPublicOfficeProofUrl(u) ||
    u.includes('/auth/office/set-password') ||
    u.includes('/auth/office/password-status') ||
    u.includes('/auth/office/verify-sms') ||
    u.includes('/auth/office/signup') ||
    u.includes('/auth/office/complete-profile') ||
    u.includes('/login-images/public')
  );
}

function clearAuthAndRedirect(router: Router): void {
  clearAuthSession();
  router.navigate(['/auth/login']);
}

function withApiCredentials(url: string, req: Parameters<HttpInterceptorFn>[0]) {
  if (!url.includes(environment.baseUrl)) {
    return req;
  }
  const headers = req.headers.has('X-Auth-Audience')
    ? req.headers
    : req.headers.set('X-Auth-Audience', 'office');
  return req.clone({ withCredentials: true, headers });
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const authService = inject(AuthService);
  const url = req.url;

  let outgoing = withApiCredentials(url, req);

  if (isPublicOfficeProofUrl(url)) {
    outgoing = outgoing.clone({ headers: outgoing.headers.delete('Authorization') });
  } else if (!outgoing.headers.has('Authorization')) {
    const token = getAccessToken();
    if (token) {
      outgoing = outgoing.clone({
        setHeaders: { Authorization: `Bearer ${token}` },
      });
    }
  }

  return next(outgoing).pipe(
    catchError((err: HttpErrorResponse) => {
      const body = err.error as { message?: string; error?: string } | undefined;
      const message = `${body?.message ?? ''} ${body?.error ?? ''}`.toLowerCase();
      if (message.includes('csrf')) {
        return throwError(() => err);
      }

      if (err.status !== 401 || isAuthExemptUrl(url)) {
        return throwError(() => err);
      }

      // BLK-35: the epoch this retry belongs to, captured before the refresh.
      const startedAtEpoch = authEpochStore.current();

      return from(authService.refreshSession()).pipe(
        switchMap((ok) => {
          if (!ok) {
            clearAuthAndRedirect(router);
            return throwError(() => err);
          }
          // Logged out while the refresh was in the air. Re-issuing now would
          // send an authenticated request after the user asked to be signed
          // out — and redirecting again would fight the logout navigation, so
          // the original error is simply surfaced.
          if (!isSessionResultCurrent(startedAtEpoch)) {
            return throwError(() => err);
          }
          const newTok = getAccessToken();
          let headers = outgoing.headers.delete('Authorization');
          if (newTok) {
            headers = headers.set('Authorization', `Bearer ${newTok}`);
          }
          const retry = outgoing.clone({ headers });
          // next() is terminal (it does not re-enter this interceptor), so there is
          // no refresh loop; if the retry still 401s, send the user to login.
          return next(retry).pipe(
            catchError((retryErr: HttpErrorResponse) => {
              if (retryErr.status === 401) {
                clearAuthAndRedirect(router);
              }
              return throwError(() => retryErr);
            }),
          );
        }),
      );
    }),
  );
};
